import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';

const CANONICAL = 'https://hoosierlifeinsurance.com/term-life-insurance-quotes-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

/* ─── Types ──────────────────────────────────────────────────────────────── */
type TLAge = '25' | '30' | '35' | '40' | '45' | '50' | '55';
type TLCoverage = '100k' | '250k' | '500k' | '750k' | '1m';
type TLTerm = '10' | '15' | '20' | '25' | '30';
type TLHealth = 'preferred_plus' | 'preferred' | 'standard_plus' | 'standard';
type TLGender = 'male' | 'female';

interface TLQuoteResult {
  carrier: string;
  amBest: string;
  monthlyPremium: number;
  annualPremium: number;
  annualSavings: number;
  rateClass: string;
  approvalTime: string;
  notes: string;
  badge?: string;
}

/* ─── Rate Engine ────────────────────────────────────────────────────────── */
const BASE_RATES: Record<TLAge, Record<TLCoverage, Record<TLTerm, number>>> = {
  '25': {
    '100k': { '10': 8, '15': 10, '20': 12, '25': 14, '30': 16 },
    '250k': { '10': 13, '15': 16, '20': 19, '25': 22, '30': 26 },
    '500k': { '10': 18, '15': 22, '20': 27, '25': 32, '30': 38 },
    '750k': { '10': 24, '15': 30, '20': 37, '25': 44, '30': 52 },
    '1m':   { '10': 30, '15': 38, '20': 46, '25': 56, '30': 66 },
  },
  '30': {
    '100k': { '10': 9, '15': 11, '20': 14, '25': 17, '30': 20 },
    '250k': { '10': 15, '15': 19, '20': 23, '25': 28, '30': 33 },
    '500k': { '10': 21, '15': 27, '20': 33, '25': 40, '30': 48 },
    '750k': { '10': 29, '15': 37, '20': 46, '25': 56, '30': 67 },
    '1m':   { '10': 36, '15': 46, '20': 57, '25': 70, '30': 84 },
  },
  '35': {
    '100k': { '10': 11, '15': 14, '20': 18, '25': 22, '30': 27 },
    '250k': { '10': 18, '15': 23, '20': 29, '25': 36, '30': 44 },
    '500k': { '10': 26, '15': 34, '20': 43, '25': 54, '30': 66 },
    '750k': { '10': 36, '15': 47, '20': 60, '25': 75, '30': 92 },
    '1m':   { '10': 45, '15': 59, '20': 75, '25': 94, '30': 115 },
  },
  '40': {
    '100k': { '10': 15, '15': 20, '20': 26, '25': 33, '30': 41 },
    '250k': { '10': 25, '15': 33, '20': 43, '25': 55, '30': 68 },
    '500k': { '10': 38, '15': 50, '20': 65, '25': 83, '30': 103 },
    '750k': { '10': 53, '15': 70, '20': 91, '25': 116, '30': 144 },
    '1m':   { '10': 66, '15': 88, '20': 114, '25': 145, '30': 180 },
  },
  '45': {
    '100k': { '10': 22, '15': 30, '20': 40, '25': 51, '30': 64 },
    '250k': { '10': 37, '15': 50, '20': 67, '25': 86, '30': 107 },
    '500k': { '10': 57, '15': 77, '20': 103, '25': 132, '30': 165 },
    '750k': { '10': 80, '15': 108, '20': 144, '25': 185, '30': 231 },
    '1m':   { '10': 100, '15': 135, '20': 180, '25': 231, '30': 289 },
  },
  '50': {
    '100k': { '10': 34, '15': 47, '20': 63, '25': 82, '30': 103 },
    '250k': { '10': 57, '15': 79, '20': 106, '25': 138, '30': 173 },
    '500k': { '10': 88, '15': 122, '20': 164, '25': 213, '30': 267 },
    '750k': { '10': 123, '15': 171, '20': 230, '25': 298, '30': 374 },
    '1m':   { '10': 154, '15': 214, '20': 288, '25': 373, '30': 467 },
  },
  '55': {
    '100k': { '10': 54, '15': 76, '20': 103, '25': 135, '30': 170 },
    '250k': { '10': 91, '15': 128, '20': 173, '25': 227, '30': 285 },
    '500k': { '10': 140, '15': 197, '20': 267, '25': 350, '30': 440 },
    '750k': { '10': 196, '15': 276, '20': 374, '25': 490, '30': 616 },
    '1m':   { '10': 245, '15': 345, '20': 467, '25': 613, '30': 770 },
  },
};

const HEALTH_MULT: Record<TLHealth, number> = {
  preferred_plus: 0.85,
  preferred: 1.0,
  standard_plus: 1.18,
  standard: 1.38,
};

const GENDER_MULT: Record<TLGender, number> = {
  male: 1.0,
  female: 0.88,
};

const COVERAGE_AMOUNTS: Record<TLCoverage, number> = {
  '100k': 100000, '250k': 250000, '500k': 500000, '750k': 750000, '1m': 1000000,
};

function getTLQuotes(age: TLAge, coverage: TLCoverage, term: TLTerm, health: TLHealth, gender: TLGender): TLQuoteResult[] {
  const base = BASE_RATES[age][coverage][term];
  const hm = HEALTH_MULT[health];
  const gm = GENDER_MULT[gender];
  const monthly = Math.round(base * hm * gm);
  const annual = monthly * 12;
  const rateClassLabel = { preferred_plus: 'Preferred Plus', preferred: 'Preferred', standard_plus: 'Standard Plus', standard: 'Standard' }[health];

  return [
    {
      carrier: 'Banner Life',
      amBest: 'A+',
      monthlyPremium: monthly,
      annualPremium: annual,
      annualSavings: Math.round(annual * 0.08),
      rateClass: rateClassLabel,
      approvalTime: '24–48 hrs (accelerated)',
      notes: 'Consistently lowest term life rates in Indianapolis. Excellent for healthy applicants. Accelerated underwriting up to $1M.',
      badge: 'Lowest Rate',
    },
    {
      carrier: 'Protective Life',
      amBest: 'A+',
      monthlyPremium: Math.round(monthly * 1.03),
      annualPremium: Math.round(monthly * 1.03) * 12,
      annualSavings: Math.round(annual * 0.06),
      rateClass: rateClassLabel,
      approvalTime: '24–72 hrs (accelerated)',
      notes: 'Excellent rates for 30-year terms. Strong for Indianapolis applicants with minor health conditions.',
    },
    {
      carrier: 'Pacific Life',
      amBest: 'A+',
      monthlyPremium: Math.round(monthly * 1.05),
      annualPremium: Math.round(monthly * 1.05) * 12,
      annualSavings: Math.round(annual * 0.04),
      rateClass: rateClassLabel,
      approvalTime: '1–2 weeks',
      notes: 'Top pick for high-coverage policies ($500K+). Competitive rates for Indianapolis professionals.',
    },
    {
      carrier: 'Prudential',
      amBest: 'A+',
      monthlyPremium: Math.round(monthly * 1.08),
      annualPremium: Math.round(monthly * 1.08) * 12,
      annualSavings: Math.round(annual * 0.02),
      rateClass: rateClassLabel,
      approvalTime: '1–3 weeks',
      notes: 'Best for complex health histories. Lenient underwriting for Indianapolis applicants with controlled conditions.',
    },
    {
      carrier: 'AIG / Corebridge',
      amBest: 'A',
      monthlyPremium: Math.round(monthly * 1.11),
      annualPremium: Math.round(monthly * 1.11) * 12,
      annualSavings: 0,
      rateClass: rateClassLabel,
      approvalTime: '1–3 weeks',
      notes: 'Competitive for older applicants (45+). Good option for Indianapolis residents with some health history.',
    },
  ];
}

/* ─── Internal Linking Cluster ───────────────────────────────────────────── */
const CLUSTER_LINKS = [
  { to: '/life-insurance-quotes-indianapolis', label: 'Life Insurance Quotes Indianapolis', icon: 'ri-search-line' },
  { to: '/term-life-insurance-indianapolis', label: 'Term Life Insurance Indianapolis', icon: 'ri-calendar-check-line' },
  { to: '/term-life-insurance-rates-indianapolis', label: 'Term Life Insurance Rates Indianapolis', icon: 'ri-bar-chart-line' },
  { to: '/cheap-life-insurance-indianapolis', label: 'Cheap Life Insurance Indianapolis', icon: 'ri-price-tag-3-line' },
  { to: '/affordable-life-insurance-indianapolis', label: 'Affordable Life Insurance Indianapolis', icon: 'ri-money-dollar-circle-line' },
  { to: '/life-insurance-rates-indianapolis', label: 'Life Insurance Rates Indianapolis', icon: 'ri-line-chart-line' },
  { to: '/no-exam-life-insurance-indianapolis', label: 'No Exam Life Insurance Indianapolis', icon: 'ri-stethoscope-line' },
  { to: '/best-life-insurance-indianapolis', label: 'Best Life Insurance Indianapolis', icon: 'ri-award-line' },
  { to: '/whole-life-insurance-quotes-indianapolis', label: 'Whole Life Insurance Quotes Indianapolis', icon: 'ri-safe-line' },
  { to: '/life-insurance-agent-indianapolis', label: 'Life Insurance Agent Indianapolis', icon: 'ri-user-star-line' },
  { to: '/life-insurance-for-families-indianapolis', label: 'Life Insurance for Families Indianapolis', icon: 'ri-home-heart-line' },
  { to: '/mortgage-life-insurance-indianapolis', label: 'Mortgage Life Insurance Indianapolis', icon: 'ri-building-line' },
];

/* ─── FAQs ───────────────────────────────────────────────────────────────── */
const FAQS = [
  {
    q: 'How much does term life insurance cost in Indianapolis?',
    a: 'Term life insurance costs in Indianapolis depend on your age, coverage amount, term length, gender, and health class. Here are sample monthly rates for a $500,000 20-year term policy for a healthy non-smoker: Age 25 male: $27/month. Age 30 male: $33/month. Age 35 male: $43/month. Age 40 male: $65/month. Age 45 male: $103/month. Age 50 male: $164/month. Women typically pay 10–15% less than men for the same coverage. An independent agent compares all available carriers to find your best term life rate in Indianapolis.',
  },
  {
    q: 'What is the best term life insurance company in Indianapolis?',
    a: 'The best term life insurance company in Indianapolis depends on your age, health, and coverage needs. For lowest rates: Banner Life and Protective Life consistently offer the most competitive term life rates in Indianapolis. For complex health histories: Prudential and Pacific Life are more lenient with underwriting. For high coverage ($1M+): Pacific Life and Protective Life are top choices. For older applicants (50+): AIG/Corebridge and Prudential offer competitive rates. For accelerated underwriting (no medical exam up to $1M): Banner Life and Protective Life. The key: there is no single best carrier for all Indianapolis residents. An independent agent compares all available carriers simultaneously to find your best fit.',
  },
  {
    q: 'How much term life insurance do I need in Indianapolis?',
    a: 'The right amount of term life insurance for Indianapolis residents depends on your financial obligations and goals. The DIME method is a good starting point: Debt: Add up all debts (mortgage, car loans, credit cards, student loans). Income: Multiply your annual income by 10–12 years. Mortgage: Add your remaining mortgage balance. Education: Add estimated college costs for each child. For a typical Indianapolis family with a $250,000 mortgage, $75,000 income, and two children, the recommended coverage is $1M–$1.5M. A 20-year term policy covers the years when your family is most financially vulnerable. An independent agent can help you calculate the exact right coverage amount for your Indianapolis family.',
  },
  {
    q: 'What is the difference between 20-year and 30-year term life insurance in Indianapolis?',
    a: 'The key differences between 20-year and 30-year term life insurance for Indianapolis residents: 20-year term: Lower monthly premiums. Covers ages 30–50 (if purchased at 30). Best for: mortgage protection, income replacement while children are young, covering a specific debt. 30-year term: Higher monthly premiums (typically 30–40% more than 20-year). Covers ages 30–60 (if purchased at 30). Best for: longer mortgage terms, younger children, wanting coverage through retirement age. For most Indianapolis families with young children and a 30-year mortgage, a 30-year term policy provides the most comprehensive protection. For Indianapolis residents in their 40s, a 20-year term is often more cost-effective. An independent agent can help you determine the right term length for your situation.',
  },
  {
    q: 'Can I get term life insurance quotes online in Indianapolis?',
    a: 'Yes — you can get term life insurance quotes online in Indianapolis. Our Term Life Quote Calculator above provides estimated rates for Indianapolis residents based on age, coverage, term, gender, and health class. For the most accurate term life quotes in Indianapolis: Use our calculator above for an instant estimate. Call (317) 648-2983 for exact quotes from all available carriers. Book a free 15-minute consultation to compare all carriers side by side. Important: online quote tools provide estimates — actual rates depend on full underwriting, which considers your complete health history, family history, occupation, and lifestyle. An independent agent can help you navigate the underwriting process and find the best rate for your specific situation.',
  },
  {
    q: 'What is accelerated underwriting for term life insurance in Indianapolis?',
    a: 'Accelerated underwriting is a process that allows Indianapolis residents to get approved for term life insurance without a medical exam — often within 24–72 hours. How it works: You complete a detailed health questionnaire online or by phone. The carrier uses data from prescription databases, MIB (Medical Information Bureau), and driving records to assess your risk. If you qualify, you receive an approval and rate class without a blood draw or physical exam. Who qualifies: Typically applicants under age 60 in good health with coverage up to $1M–$3M (varies by carrier). Banner Life and Protective Life offer the most competitive accelerated underwriting programs in Indianapolis. Who should get a full medical exam: Applicants with complex health histories may actually get better rates with a full exam — a doctor\'s exam can sometimes result in a better rate class than accelerated underwriting.',
  },
  {
    q: 'What term life insurance riders are available in Indianapolis?',
    a: 'Term life insurance riders in Indianapolis can significantly enhance your policy\'s value. Common riders available: Waiver of Premium Rider: Waives your premium if you become disabled — your policy stays in force without payment. Accelerated Death Benefit Rider: Allows you to access a portion of your death benefit if diagnosed with a terminal illness. Child Rider: Adds term coverage for all your children under one rider — typically $10,000–$25,000 per child. Accidental Death Benefit Rider: Pays an additional death benefit if you die in an accident. Return of Premium Rider: Returns all premiums paid if you outlive the policy term — significantly increases monthly cost. Conversion Rider: Allows you to convert your term policy to permanent life insurance without a medical exam — valuable if your health changes. The Conversion Rider is particularly valuable for Indianapolis residents who may want permanent coverage later but can\'t afford it now.',
  },
  {
    q: 'How do I compare term life insurance quotes in Indianapolis?',
    a: 'Comparing term life insurance quotes in Indianapolis requires looking at more than just the monthly premium. Key factors to compare: Monthly premium: The amount you pay each month — but this is just the starting point. Rate class: Preferred Plus, Preferred, Standard Plus, or Standard — the rate class determines your premium. AM Best rating: Financial strength of the carrier — stick with A or A+ rated carriers. Conversion options: Can you convert to permanent coverage without a medical exam? Accelerated underwriting: Can you get approved without a medical exam? Riders available: What optional riders are available and at what cost? The most important step: work with an independent agent who can compare all available carriers simultaneously. Call Hoosier Life Insurance at (317) 648-2983 for a free term life quote comparison.',
  },
  {
    q: 'What health conditions affect term life insurance rates in Indianapolis?',
    a: 'Many health conditions affect term life insurance rates for Indianapolis residents. Conditions that typically result in higher rates: High blood pressure (controlled): Standard Plus to Standard rate class — 15–40% above Preferred rates. Type 2 diabetes (controlled): Standard to Table 2 rate class — 40–100% above Preferred rates. High cholesterol (controlled): Standard Plus rate class — 15–25% above Preferred rates. Sleep apnea (treated with CPAP): Standard Plus to Standard rate class. Overweight/obesity: Standard Plus to Standard rate class depending on BMI. Conditions that may result in decline: Uncontrolled diabetes. Recent cancer (within 5 years for most carriers). Recent heart attack or stroke (within 2–5 years). Active substance abuse. The key: different carriers treat the same condition differently. An independent agent who specializes in impaired risk underwriting can find the best carrier for your specific health history.',
  },
  {
    q: 'What is the best term length for term life insurance in Indianapolis?',
    a: 'The best term length for Indianapolis residents depends on your specific financial obligations and goals. 10-year term: Best for: covering a specific short-term debt, supplementing existing coverage, older applicants (50+) who need affordable coverage. 15-year term: Best for: covering a 15-year mortgage, children who will be independent in 15 years. 20-year term: Most popular choice for Indianapolis families. Best for: 30-year mortgages (covers the highest-risk years), income replacement while children are young, covering ages 30–50. 25-year term: Best for: younger Indianapolis residents (25–35) who want coverage through age 50–60. 30-year term: Best for: younger Indianapolis residents (25–35) who want the longest possible coverage, 30-year mortgages, wanting coverage through retirement age. The most important factor: your term should last at least as long as your longest financial obligation (typically your mortgage).',
  },
];

/* ─── Testimonials ───────────────────────────────────────────────────────── */
const TESTIMONIALS = [
  {
    name: 'Ryan & Ashley T.',
    location: 'Fishers, Indianapolis',
    quote: 'We needed $750K in coverage for our mortgage and two kids. Benjamin compared 6 carriers and found us Banner Life at $67/month. We had no idea it could be that affordable. Approved in 48 hours — no medical exam.',
    stars: 5,
    coverage: '$750K 30-year term',
    savings: 'Saved $31/mo vs. State Farm',
  },
  {
    name: 'Marcus J.',
    location: 'Carmel, Indianapolis',
    quote: 'I\'m 45 and thought term life would be expensive. Benjamin found me a $500K 20-year policy for $103/month. That\'s less than my car insurance. My family is protected through my kids\' college years.',
    stars: 5,
    coverage: '$500K 20-year term',
    savings: 'Approved in 3 days',
  },
  {
    name: 'Sarah & Mike D.',
    location: 'Noblesville, Indianapolis',
    quote: 'We had high blood pressure and were worried about getting approved. Benjamin knew exactly which carriers are lenient with BP. We both got Standard Plus rates — much better than we expected.',
    stars: 5,
    coverage: '$500K each, 20-year term',
    savings: 'Standard Plus despite BP',
  },
  {
    name: 'Jennifer K.',
    location: 'Broad Ripple, Indianapolis',
    quote: 'Single mom, needed affordable coverage for my kids. Benjamin found me $500K for $33/month at age 30. I can\'t believe how easy it was. The whole process took less than a week.',
    stars: 5,
    coverage: '$500K 20-year term',
    savings: '$33/month at age 30',
  },
];

/* ─── Component ─────────────────────────────────────────────────────────── */
export default function TermLifeInsuranceQuotesIndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showCluster, setShowCluster] = useState(false);

  const [tlAge, setTlAge] = useState<TLAge>('35');
  const [tlCoverage, setTlCoverage] = useState<TLCoverage>('500k');
  const [tlTerm, setTlTerm] = useState<TLTerm>('20');
  const [tlHealth, setTlHealth] = useState<TLHealth>('preferred');
  const [tlGender, setTlGender] = useState<TLGender>('male');
  const [quotes, setQuotes] = useState<TLQuoteResult[] | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const title = 'Term Life Insurance Quotes Indianapolis IN | Instant Rate Calculator | Hoosier Life Insurance';
    const description = 'Get term life insurance quotes in Indianapolis, IN. Compare rates from Banner Life, Protective, Pacific Life & more. Use our instant Term Quote Calculator. Call (317) 648-2983.';
    const keywords = 'term life insurance quotes Indianapolis, term life insurance Indianapolis Indiana, term life insurance rates Indianapolis, cheap term life insurance Indianapolis';
    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'last-modified', content: new Date().toISOString() },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/ff5dd3afdd37e1999a7ca5a93813540c.jpg' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
    ];

    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });

    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: CANONICAL,
        inLanguage: 'en-US',
        dateModified: '2026-04-12T00:00:00Z',
        isPartOf: { '@type': 'WebSite', name: 'Hoosier Life Insurance', url: SITE_URL },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Life Insurance Indianapolis', item: `${SITE_URL}/life-insurance-indianapolis` },
          { '@type': 'ListItem', position: 3, name: 'Term Life Insurance Quotes Indianapolis', item: CANONICAL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
    ];

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schemas);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  const ageOptions: TLAge[] = ['25', '30', '35', '40', '45', '50', '55'];
  const coverageOptions: { value: TLCoverage; label: string }[] = [
    { value: '100k', label: '$100K' },
    { value: '250k', label: '$250K' },
    { value: '500k', label: '$500K' },
    { value: '750k', label: '$750K' },
    { value: '1m', label: '$1M' },
  ];
  const termOptions: { value: TLTerm; label: string }[] = [
    { value: '10', label: '10 yr' },
    { value: '15', label: '15 yr' },
    { value: '20', label: '20 yr' },
    { value: '25', label: '25 yr' },
    { value: '30', label: '30 yr' },
  ];
  const healthOptions: { value: TLHealth; label: string; desc: string }[] = [
    { value: 'preferred_plus', label: 'Preferred Plus', desc: 'Excellent health, no conditions' },
    { value: 'preferred', label: 'Preferred', desc: 'Very good health, minor issues' },
    { value: 'standard_plus', label: 'Standard Plus', desc: 'Good health, some conditions' },
    { value: 'standard', label: 'Standard', desc: 'Average health, controlled conditions' },
  ];

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <Link to="/term-life-insurance-indianapolis" className="text-gray-500 hover:text-[#D97642] transition-colors whitespace-nowrap">
            Term Life Insurance Indianapolis
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">Term Life Quotes</span>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden min-h-[560px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=Indianapolis%20Indiana%20young%20professional%20couple%20in%20their%2030s%20reviewing%20term%20life%20insurance%20quotes%20on%20laptop%20at%20modern%20kitchen%20table%20warm%20natural%20light%20suburban%20home%20Marion%20County%20Indiana%20relaxed%20confident%20financial%20planning%20family%20protection%20photorealistic%20warm%20neutral%20tones%20cream%20beige%20background%20wide%20angle&width=1440&height=700&seq=tl-quotes-indy-hero-2025a&orientation=landscape"
            alt="Term Life Insurance Quotes Indianapolis Indiana — Instant Rate Calculator"
            title="Term Life Insurance Quotes in Indianapolis, Indiana"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={700}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/96 via-[#1A3A52]/85 to-[#1A3A52]/30"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 w-full">
          <div className="max-w-[720px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-calendar-check-line text-sm"></i>
              Indianapolis &amp; Marion County · Term Life Insurance · 5 Top Carriers · Instant Quotes
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Term Life Insurance Quotes —{' '}
              <span className="text-[#D97642]">Compare 20+ Carriers in Indianapolis</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-6 max-w-[640px]">
              Compare term life insurance quotes from the top carriers in Indianapolis. Use our <strong className="text-white">Instant Term Quote Calculator</strong> to see rates from Banner Life, Protective, Pacific Life, Prudential, and AIG side by side.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { value: '$18/mo', label: 'Starting rate for healthy 30-year-old in Indianapolis' },
                { value: '5', label: 'Top carriers compared side by side' },
                { value: '24 hrs', label: 'Approval time with accelerated underwriting' },
                { value: 'Free', label: 'Quote comparison — no obligation' },
              ].map(item => (
                <div key={item.label} className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-xl px-4 py-3 text-center">
                  <div className="text-xl font-extrabold text-[#D97642]">{item.value}</div>
                  <div className="text-xs text-white/80 mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <a href="#quote-calculator" className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-calculator-line text-lg"></i>
                Get My Term Life Quote
              </a>
              <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1A3A52] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { value: '$18/mo', label: 'Lowest term life rate in Indianapolis — healthy 30-year-old, $250K, 20-year term' },
              { value: '10–30yr', label: 'Term lengths available — choose the coverage period that matches your needs' },
              { value: 'A/A+', label: 'All carriers are A or A+ rated by AM Best — financially strong and reliable' },
              { value: '48 hrs', label: 'Fastest approval time — accelerated underwriting, no medical exam required' },
            ].map(stat => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <span className="text-2xl md:text-3xl font-extrabold text-[#D97642]">{stat.value}</span>
                <span className="text-xs text-white/70 leading-snug max-w-[160px]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Takeaways */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-fill text-[#D97642] text-2xl"></i>
            </div>
            <div className="flex-1">
              <h2 className="text-[#1A3A52] font-bold text-base mb-3">Key Facts: Term Life Insurance Quotes in Indianapolis</h2>
              <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                {[
                  'Term life insurance is the most affordable type of life insurance — a healthy 35-year-old can get $500K for as little as $26/month',
                  'Rates are locked in at purchase — your premium never increases for the entire term (10, 15, 20, 25, or 30 years)',
                  'Banner Life and Protective Life consistently offer the lowest term life rates in Indianapolis',
                  'Accelerated underwriting allows approval in 24–72 hours without a medical exam for healthy applicants',
                  'Women pay 10–15% less than men for the same term life coverage in Indianapolis',
                  'An independent agent compares all carriers simultaneously — some are 15–25% cheaper than others for the same coverage',
                ].map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-600 text-sm">
                    <i className="ri-arrow-right-s-line text-[#D97642] flex-shrink-0 mt-0.5"></i>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-gray-400 text-xs">Last reviewed April 2026 · Licensed Life Insurance Specialists at Hoosier Life Insurance · Indiana Licensed · Independent — 20+ Carriers</div>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT + SIDEBAR */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 xl:gap-16 items-start">

          {/* LEFT */}
          <div>

            {/* QUOTE CALCULATOR */}
            <div id="quote-calculator" className="mb-16 scroll-mt-24">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Instant Term Quote Calculator</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3 leading-tight">
                Compare Term Life Insurance Quotes — Indianapolis
              </h2>
              <p className="text-sm text-gray-500 mb-8">
                Select your age, coverage, term, gender, and health class to see estimated term life rates from the top 5 carriers in Indianapolis.
              </p>

              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-[#1A3A52] px-7 py-7">
                  <h3 className="text-base font-bold text-white mb-6 flex items-center gap-2">
                    <i className="ri-calculator-line text-[#D97642]"></i>
                    Your Profile
                  </h3>

                  {/* Age */}
                  <div className="mb-5">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Your Age</label>
                    <div className="flex flex-wrap gap-2">
                      {ageOptions.map(a => (
                        <button key={a} onClick={() => { setTlAge(a); setQuotes(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${tlAge === a ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {a}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Coverage */}
                  <div className="mb-5">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Coverage Amount</label>
                    <div className="flex flex-wrap gap-2">
                      {coverageOptions.map(opt => (
                        <button key={opt.value} onClick={() => { setTlCoverage(opt.value); setQuotes(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${tlCoverage === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Term */}
                  <div className="mb-5">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Term Length</label>
                    <div className="flex flex-wrap gap-2">
                      {termOptions.map(opt => (
                        <button key={opt.value} onClick={() => { setTlTerm(opt.value); setQuotes(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${tlTerm === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Gender */}
                  <div className="mb-5">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Gender</label>
                    <div className="flex gap-3">
                      {(['male', 'female'] as TLGender[]).map(g => (
                        <button key={g} onClick={() => { setTlGender(g); setQuotes(null); }}
                          className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer capitalize ${tlGender === g ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {g === 'male' ? 'Male' : 'Female (10–15% lower)'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Health */}
                  <div className="mb-7">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Health Class</label>
                    <div className="grid grid-cols-2 gap-3">
                      {healthOptions.map(opt => (
                        <button key={opt.value} onClick={() => { setTlHealth(opt.value); setQuotes(null); }}
                          className={`px-4 py-3 rounded-xl text-left transition-all cursor-pointer ${tlHealth === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          <div className="text-sm font-bold">{opt.label}</div>
                          <div className={`text-xs mt-0.5 ${tlHealth === opt.value ? 'text-white/80' : 'text-white/50'}`}>{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <button onClick={() => setQuotes(getTLQuotes(tlAge, tlCoverage, tlTerm, tlHealth, tlGender))}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold text-base">
                    <i className="ri-calculator-line text-lg"></i>
                    Calculate My Term Life Quote
                  </button>
                </div>

                {/* Results */}
                {quotes !== null && (
                  <div className="px-7 py-7">
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
                      <h3 className="text-base font-bold text-[#1A3A52]">
                        Term Life Quotes — ${COVERAGE_AMOUNTS[tlCoverage].toLocaleString()} · {tlTerm}-Year · Age {tlAge} · {tlGender === 'male' ? 'Male' : 'Female'}
                      </h3>
                      {quotes.length > 0 && (
                        <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#4A7C59]/10 text-[#4A7C59] whitespace-nowrap">
                          From ${Math.min(...quotes.map(q => q.monthlyPremium))}/mo
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col gap-4 mb-6">
                      {quotes.map((q, i) => (
                        <div key={q.carrier} className={`rounded-xl border p-5 ${i === 0 ? 'border-[#4A7C59]/30 bg-[#F0FFF4]' : 'border-gray-100 bg-gray-50'}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 flex items-center justify-center rounded-full text-white text-sm font-bold flex-shrink-0 ${i === 0 ? 'bg-[#4A7C59]' : 'bg-[#1A3A52]/30'}`}>{i + 1}</div>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-bold text-[#1A3A52]">{q.carrier}</span>
                                  {q.badge && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#4A7C59] text-white whitespace-nowrap">{q.badge}</span>}
                                  <span className="text-xs bg-[#1A3A52]/10 text-[#1A3A52] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">AM Best {q.amBest}</span>
                                  <span className="text-xs bg-[#D97642]/10 text-[#D97642] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">{q.rateClass}</span>
                                </div>
                                <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{q.notes}</p>
                                <p className="text-xs text-gray-400 mt-0.5">Approval: {q.approvalTime}</p>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className={`text-xl font-extrabold ${i === 0 ? 'text-[#4A7C59]' : 'text-[#1A3A52]'}`}>${q.monthlyPremium}<span className="text-sm font-normal">/mo</span></div>
                              <div className="text-xs text-gray-400">${q.annualPremium.toLocaleString()}/yr</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="bg-[#1A3A52]/5 rounded-xl p-4 mb-5">
                      <p className="text-xs text-gray-500 leading-relaxed">
                        <strong className="text-[#1A3A52]">Disclaimer:</strong> Rates are estimates for illustration purposes. Actual rates depend on full underwriting, health history, and carrier selection. Call <a href="tel:+13176482983" className="text-[#D97642] font-semibold">(317) 648-2983</a> for your exact personalized term life quote.
                      </p>
                    </div>

                    <button onClick={() => setShowBookingModal(true)}
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                      <i className="ri-calendar-check-line text-lg"></i>
                      Get My Exact Term Life Quote — Free 15-Min Call
                    </button>
                  </div>
                )}

                {quotes === null && (
                  <div className="px-7 py-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D97642]/10 mx-auto mb-4">
                      <i className="ri-calendar-check-line text-3xl text-[#D97642]"></i>
                    </div>
                    <p className="text-sm text-gray-500">Select your profile above, then click <strong className="text-[#1A3A52]">Calculate My Term Life Quote</strong>.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Rate Table */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Rate Reference</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Term Life Insurance Rates in Indianapolis — $500K Coverage
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                Sample monthly rates for a $500,000 term life policy for healthy non-smokers in Indianapolis. Rates shown are for Preferred health class.
              </p>
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="bg-[#1A3A52] px-6 py-4 grid grid-cols-6 gap-2 text-xs font-semibold text-white/70 uppercase tracking-wide">
                  <div>Age</div>
                  <div className="text-center">10 yr</div>
                  <div className="text-center">15 yr</div>
                  <div className="text-center">20 yr</div>
                  <div className="text-center">25 yr</div>
                  <div className="text-center">30 yr</div>
                </div>
                <div className="divide-y divide-gray-100">
                  {(['25', '30', '35', '40', '45', '50', '55'] as TLAge[]).map((age, i) => (
                    <div key={age} className={`px-6 py-3 grid grid-cols-6 gap-2 items-center ${i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}`}>
                      <div className="text-sm font-bold text-[#1A3A52]">Age {age}</div>
                      {(['10', '15', '20', '25', '30'] as TLTerm[]).map(term => {
                        const rate = Math.round(BASE_RATES[age]['500k'][term] * HEALTH_MULT['preferred'] * GENDER_MULT['male']);
                        return (
                          <div key={term} className="text-center">
                            <span className="text-sm font-semibold text-[#1A3A52]">${rate}</span>
                            <span className="text-xs text-gray-400">/mo</span>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-3">Male, Preferred health class, non-smoker. Women pay approximately 10–15% less. Rates vary by carrier and full underwriting.</p>
            </div>

            {/* Testimonials */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Stories</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Indianapolis Families Who Got Their Term Life Quote
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {TESTIMONIALS.map(t => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <span className="bg-[#D97642]/10 text-[#D97642] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.coverage}</span>
                      <span className="bg-[#4A7C59]/10 text-[#4A7C59] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.savings}</span>
                    </div>
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => <i key={i} className="ri-star-fill text-[#D97642] text-sm"></i>)}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                        <i className="ri-user-fill text-sm text-[#1A3A52]"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#1A3A52]">{t.name}</p>
                        <p className="text-xs text-gray-400">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Internal Linking Cluster */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Indianapolis Life Insurance Guide</span>
              <h2 className="text-2xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Explore All Indianapolis Life Insurance Topics
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Compare rates, explore coverage options, and find the right policy for your Indianapolis family.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {CLUSTER_LINKS.map(link => (
                  <Link key={link.to} to={link.to} className="flex items-center gap-3 px-4 py-3.5 bg-white rounded-xl border border-gray-100 hover:border-[#D97642]/30 hover:bg-[#FFF8F4] transition-all group cursor-pointer">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-[#1A3A52]">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Term Life Insurance Quotes Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors">
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug">
                        <a id={`faq-tl-quotes-indy-${i}`}>{faq.q}</a>
                      </h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">Free Term Life Quote</h3>
                    <p className="text-xs text-white/70">5 top carriers compared · No obligation</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Instant estimates</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ All carriers compared</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ No obligation</span>
                </div>
              </div>
              <div className="px-7 py-7">
                <div className="flex flex-col gap-3">
                  <button onClick={() => setShowBookingModal(true)}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Consultation
                  </button>
                  <a href="tel:+13176482983"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1A3A52] text-white rounded-xl hover:bg-[#0F2A3F] transition-all cursor-pointer font-bold">
                    <i className="ri-phone-fill text-lg"></i>
                    Call (317) 648-2983
                  </a>
                  <Link to="/instant-quote"
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-[#1A3A52] rounded-xl hover:bg-gray-200 transition-all cursor-pointer font-semibold text-sm">
                    <i className="ri-file-list-3-line"></i>
                    Get Instant Online Quote
                  </Link>
                </div>
                <p className="text-xs text-gray-400 text-center mt-4">Free · No obligation · All ages welcome</p>
              </div>
            </div>

            {/* Quick Rate Reference */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4 flex items-center gap-2">
                <i className="ri-price-tag-3-line text-[#D97642]"></i>
                Quick Rate Reference — $500K 20-Year Term
              </h3>
              <div className="flex flex-col gap-2">
                {[
                  { label: 'Age 25, Male', rate: '$27/mo' },
                  { label: 'Age 30, Male', rate: '$33/mo' },
                  { label: 'Age 35, Male', rate: '$43/mo' },
                  { label: 'Age 40, Male', rate: '$65/mo' },
                  { label: 'Age 45, Male', rate: '$103/mo' },
                  { label: 'Age 50, Male', rate: '$164/mo' },
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <span className="text-xs font-semibold text-[#1A3A52]">{row.label}</span>
                    <span className="text-xs font-bold text-[#4A7C59]">{row.rate}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-3">Preferred health class, non-smoker. Women pay ~12% less.</p>
            </div>

            {/* Booking Calendar */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free 15-Min Term Life Call</h3>
                    <p className="text-xs text-white/70">All carriers compared · No pressure</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Term Life Quote Call',
                    step0Title: 'Pick a Date', step1Title: 'Choose a Time', step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date', selectTime: 'See Available Times →', reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev', nextMonth: 'Next', noSlotsAvailable: 'No slots available',
                    selectedDateTime: 'Your Appointment', modifyTime: 'Change',
                    customerName: 'Full Name', customerPhone: 'Phone Number',
                    notes: 'Coverage amount or questions? (Optional)',
                    submitButton: 'Confirm My Free Term Life Call', submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.', backButton: '← Back to Calendar', required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>

            {/* Related Links */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4">Related Pages</h3>
              <div className="flex flex-col gap-2">
                {[
                  { to: '/term-life-insurance-indianapolis', label: 'Term Life Insurance Indianapolis', icon: 'ri-calendar-check-line' },
                  { to: '/term-life-insurance-rates-indianapolis', label: 'Term Life Rates Indianapolis', icon: 'ri-bar-chart-line' },
                  { to: '/cheap-life-insurance-indianapolis', label: 'Cheap Life Insurance Indianapolis', icon: 'ri-price-tag-3-line' },
                  { to: '/life-insurance-quotes-indianapolis', label: 'Compare All Carriers', icon: 'ri-search-line' },
                  { to: '/no-exam-life-insurance-indianapolis', label: 'No Exam Life Insurance', icon: 'ri-stethoscope-line' },
                ].map(link => (
                  <Link key={link.to} to={link.to} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F9F7F4] transition-colors group">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm text-gray-600 group-hover:text-[#1A3A52] font-medium">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Ready to see your term life insurance quote in Indianapolis?
          </h2>
          <p className="text-base text-white/75 mb-7">
            Use our Term Quote Calculator above, then call for your exact personalized quote. 5 A/A+ rated carriers compared. No obligation.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#quote-calculator" className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-calculator-line text-lg"></i>
              Calculate My Quote
            </a>
            <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-lg"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={143} productName="Term Life Insurance Quotes Indianapolis" />
      <Footer />
      <MobileProductCTABar productName="Term Life Insurance" quoteHref="/instant-quote" />

      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">Get Your Term Life Insurance Quote — Indianapolis</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance" style={{ width: '100%', height: '600px', border: 'none' }} title="Book Appointment" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
